<template>
  <div class="tw-relative">
    <WdsMenu placement="bottom-start" class="tw-z-[100]" z-index="50">
      <template #trigger>
        <button type="button" class="tw-appearance-none tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer">
          <span class="tw-flex tw-items-center tw-text-base" :class="{ 'tw-text-blue-600': hasActiveFilters, 'tw-text-gray-900': !hasActiveFilters }">
            <WdsIcon name="filter" :size="16" class="tw-mr-2" :class="{ 'tw-text-blue-600': hasActiveFilters, 'tw-text-gray-900': !hasActiveFilters }" />
            {{ $t('Filter') }}
            <template v-if="hasActiveFilters">
              <WdsBadge
                class="tw-ml-2"
                variant="primary"
                size="sm"
                data-test="active-filters-count"
              >
                {{ activeFiltersCount }}
              </WdsBadge>
            </template>
          </span>
        </button>
      </template>

      <div class="tw-bg-white tw-shadow-lg tw-rounded-md tw-w-80 tw-border tw-border-gray-200 tw-z-50">
        <div class="tw-p-4 tw-border-b tw-border-gray-200">
          <WdsStack justify-space-between align-center>
            <WdsText large>{{ $t('Filters') }}</WdsText>
            <WdsButtonGhost
              v-if="hasActiveFilters"
              class="tw-text-sm"
              data-test="reset-filters"
              @click="resetFilters"
            >
              {{ $t('Reset all') }}
            </WdsButtonGhost>
          </WdsStack>
        </div>

        <div class="tw-max-h-[60vh] tw-overflow-y-auto">
          <div v-for="(group, groupIndex) in filterGroups" :key="groupIndex" class="tw-p-4 tw-border-b tw-border-gray-200">
            <WdsText class="tw-font-medium tw-mb-2">{{ group.title }}</WdsText>

            <WdsStack v-if="group.type === 'checkboxes'" direction-column gap-sm>
              <div v-for="(option, optionIndex) in group.options" :key="`${groupIndex}-${optionIndex}`" class="tw-mt-1">
                <WdsCheckbox
                  :model-value="isFilterActive(group.key, option.value)"
                  :label="option.label"
                  data-test="filter-checkbox"
                  @update:model-value="(value) => updateFilter(group.key, option.value, value)"
                />
              </div>
            </WdsStack>

            <WdsSelect
              v-else-if="group.type === 'select'"
              v-model="filters[group.key]"
              :list="group.options"
              :label="group.title"
              multi-select
              searchable
              can-deselect
              labels-primary
              class="tw-mt-1"
              data-test="filter-select"
            />

            <div v-else-if="group.type === 'dateRange'">
              <WdsStack direction-column gap-sm>
                <WdsInput
                  type="date"
                  :label="$t('From')"
                  :model-value="filters[group.key]?.from || ''"
                  class="tw-mt-1"
                  data-test="filter-date-from"
                  @update:model-value="(value) => updateDateFilter(group.key, 'from', value)"
                />
                <WdsInput
                  type="date"
                  :label="$t('To')"
                  :model-value="filters[group.key]?.to || ''"
                  class="tw-mt-1"
                  data-test="filter-date-to"
                  @update:model-value="(value) => updateDateFilter(group.key, 'to', value)"
                />
              </WdsStack>
            </div>
          </div>
        </div>

        <div class="tw-p-4 tw-border-t tw-border-gray-200">
          <WdsStack justify-end>
            <WdsButton
              variant="secondary"
              data-test="filter-cancel"
              @click="cancelFilters"
            >
              {{ $t('Cancel') }}
            </WdsButton>
            <WdsButton
              variant="primary"
              data-test="filter-apply"
              @click="applyFilters"
            >
              {{ $t('Apply') }}
            </WdsButton>
          </WdsStack>
        </div>
      </div>
    </WdsMenu>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, defineProps, defineEmits, watch } from 'vue'
import {
  WdsBadge,
  WdsButton,
  WdsButtonGhost,
  WdsCheckbox,
  WdsIcon,
  WdsInput,
  WdsMenu,
  WdsSelect,
  WdsStack,
  WdsText
} from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

interface FilterOption {
  label: string;
  value: string | number | boolean;
}

interface FilterGroup {
  key: string;
  title: string;
  type: 'checkboxes' | 'select' | 'dateRange';
  options?: FilterOption[];
}

const props = defineProps<{
  filterGroups: FilterGroup[];
  modelValue: Record<string, any>;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: Record<string, any>): void;
  (e: 'apply', value: Record<string, any>): void;
}>();

const filters = ref<Record<string, any>>({...props.modelValue});

// When the modelValue changes externally, update our local filters
watch(() => props.modelValue, (newValue) => {
  filters.value = {...newValue};
}, { deep: true });

const resetFilters = () => {
  // Clear all filters
  filters.value = {};
  emit('update:modelValue', {});
  emit('apply', {});
};

const cancelFilters = () => {
  // Reset to original values if canceled
  filters.value = {...props.modelValue};
};

const applyFilters = () => {
  emit('update:modelValue', {...filters.value});
  emit('apply', {...filters.value});
};

// For checkbox type filters
const isFilterActive = (groupKey: string, value: any): boolean => {
  if (!filters.value[groupKey]) return false;

  if (Array.isArray(filters.value[groupKey])) {
    return filters.value[groupKey].includes(value);
  }

  return filters.value[groupKey] === value;
};

const updateFilter = (groupKey: string, value: any, checked: boolean) => {
  // Initialize if needed
  if (!filters.value[groupKey]) {
    filters.value[groupKey] = [];
  }

  // Add or remove from array
  if (checked) {
    if (Array.isArray(filters.value[groupKey]) && !filters.value[groupKey].includes(value)) {
      filters.value[groupKey].push(value);
    } else {
      filters.value[groupKey] = [value];
    }
  } else if (Array.isArray(filters.value[groupKey])) {
    filters.value[groupKey] = filters.value[groupKey].filter((v: any) => v !== value);
    // Clean up empty arrays
    if (filters.value[groupKey].length === 0) {
      delete filters.value[groupKey];
    }
  }
};

// For date range filters
const updateDateFilter = (groupKey: string, rangeKey: 'from' | 'to', value: string) => {
  if (!filters.value[groupKey]) {
    filters.value[groupKey] = {};
  }

  filters.value[groupKey][rangeKey] = value;

  // Clean up empty date ranges
  if (!filters.value[groupKey].from && !filters.value[groupKey].to) {
    delete filters.value[groupKey];
  }
};

// Count active filters to display in the badge
const activeFiltersCount = computed(() => {
  let count = 0;

  for (const key in filters.value) {
    if (filters.value[key]) {
      if (Array.isArray(filters.value[key])) {
        count += filters.value[key].length;
      } else if (typeof filters.value[key] === 'object' && !Array.isArray(filters.value[key])) {
        // For date ranges, count each defined value
        for (const subKey in filters.value[key]) {
          if (filters.value[key][subKey]) {
            count += 1;
          }
        }
      } else {
        count += 1;
      }
    }
  }

  return count;
});

// Check if there are any active filters
const hasActiveFilters = computed(() => activeFiltersCount.value > 0);
</script>
